<div class="household-detail-container">
  <div class="page-header">
    <div>
      <a routerLink="/access-management/groups" class="back-link">
        <i class="pi pi-arrow-left"></i> Back to groups
      </a>
      <h1>Household Detail</h1>
    </div>
    <div class="header-actions">
      <p-button label="Refresh" icon="pi pi-refresh" severity="primary" [text]="true" (onClick)="refreshAll()"></p-button>
      <p-button label="Delete household" icon="pi pi-trash" severity="danger" [outlined]="true" (onClick)="openDeleteDialog()"></p-button>
    </div>
  </div>

  <p-card class="overview-card" header="Household">
    @if (groupResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '40px', height: '40px' }"></p-progressSpinner></div>
    } @else if (groupResource.error()) {
      <p-message severity="error" text="Failed to load household details."></p-message>
    } @else if (groupResource.value(); as group) {
      <div class="detail-grid">
        <div class="detail-row">
          <span class="detail-label">Name</span>
          <span class="detail-value">{{ group.name }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Type</span>
          <span class="detail-value">{{ groupTypeLabel(group.groupType) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Group ID</span>
          <span class="detail-value monospace">{{ group.groupId }}</span>
        </div>
        @if (group.description) {
          <div class="detail-row">
            <span class="detail-label">Description</span>
            <span class="detail-value">{{ group.description }}</span>
          </div>
        }
        <div class="detail-row">
          <span class="detail-label">Payment Account</span>
          <span class="detail-value monospace">{{ group.paymentAccountId }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Created</span>
          <span class="detail-value">{{ formatDate(group.createdOn) }}</span>
        </div>
        <div class="detail-row">
          <span class="detail-label">Status</span>
          <span class="detail-value">
            <p-tag [value]="group.isActive ? 'Active' : 'Inactive'" [severity]="group.isActive ? 'success' : 'secondary'"></p-tag>
          </span>
        </div>
      </div>
    } @else {
      <p-message severity="warn" text="Household not found."></p-message>
    }
  </p-card>

  <p-card header="Members">
    @if (membersResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (membersResource.error()) {
      <p-message severity="error" text="Failed to load members."></p-message>
    } @else if ((membersResource.value() ?? []).length === 0) {
      <p class="muted">No members in this household yet.</p>
    } @else {
      <ul class="member-list">
        @for (m of membersResource.value(); track m.groupMemberId) {
          <li>
            <span class="member-name">{{ m.user?.name || m.userId }}</span>
            @if (m.user?.email) {
              <span class="member-email">{{ m.user?.email }}</span>
            }
            @if (m.role?.name) {
              <p-tag [value]="m.role!.name" severity="info"></p-tag>
            }
          </li>
        }
      </ul>
    }
  </p-card>

  <p-card header="Memberships">
    @if (membershipsResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (membershipsResource.error()) {
      <p-message severity="error" text="Failed to load memberships."></p-message>
    } @else if (!hasMemberships()) {
      <p class="muted">No memberships allocated to this household yet.</p>
    } @else if (membershipsResource.value(); as data) {
      <div class="totals">
        <span><strong>{{ data.totalUsers }}</strong> users</span>
        <span><strong>{{ data.totalActiveMemberships }}</strong> active memberships</span>
      </div>
      @for (user of data.memberships; track user.userId) {
        <div class="membership-user">
          <h4>{{ user.name || user.email }}</h4>
          @for (mem of user.memberships; track mem.membershipId) {
            <div class="membership-row">
              <div class="membership-header">
                <span class="package-name">{{ mem.packageName }}</span>
                <p-tag [value]="mem.status" [severity]="mem.isActive ? 'success' : 'secondary'"></p-tag>
              </div>
              @if (mem.features.length > 0) {
                <ul class="feature-list">
                  @for (f of mem.features; track f.featureId) {
                    <li>{{ f.featureName }} <small class="muted">({{ f.optionName }})</small></li>
                  }
                </ul>
              }
              <small class="muted">Assigned {{ formatDate(mem.assignedOn) }}</small>
            </div>
          }
        </div>
      }
    }
  </p-card>

  <p-card header="Recent Invitations">
    @if (invitationsResource.isLoading()) {
      <div class="loading"><p-progressSpinner [style]="{ width: '32px', height: '32px' }"></p-progressSpinner></div>
    } @else if (invitationsResource.error()) {
      <p-message severity="error" text="Failed to load invitations."></p-message>
    } @else if ((invitationsResource.value() ?? []).length === 0) {
      <p class="muted">No invitations for this household.</p>
    } @else {
      <ul class="invitation-list">
        @for (inv of invitationsResource.value(); track inv.invitationId) {
          <li>
            <span class="invitee">{{ inv.inviteeEmail }}</span>
            <p-tag [value]="inv.status" [severity]="getInvitationStatusSeverity(inv.status)"></p-tag>
            <small class="muted">{{ formatDate(inv.createdOn) }}</small>
          </li>
        }
      </ul>
    }
  </p-card>

  <p-card header="Order History">
    <p-message
      severity="info"
      text="Order history coming soon — OrdersEngine integration not yet wired."
    ></p-message>
  </p-card>

  <app-delete-entity-dialog
    entityKind="household"
    [(visible)]="showDeleteDialog"
    [entityName]="groupResource.value()?.name ?? ''"
    [submitting]="deleting()"
    (confirm)="onConfirmDelete($event)"
  ></app-delete-entity-dialog>
</div>
